/**
 * @file index.tsx
 * @fileoverview The job alert context.
 */

import { useMutation } from '@apollo/client';
import { useRouter } from 'next/router';
import React, {
  Context,
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useFlags } from '~/jobiqo/hooks/flags';
import { useMessages } from '~/jobiqo/hooks/messages';
import { Facet, FacetConfig } from '~/jobiqo/models/search';
import { JobAlert } from '~/jobiqo/types/types';
import {
  analyticsDispatch,
  EVENTS,
  PAGE_TYPES,
  PageType,
} from '~/jobiqo/lib/analytics';
import { UserContext, UserContextInterface } from '~/jobiqo/context/user';
import { getjobAlertDimensions } from '~/jobiqo/components/JobAlert/utils/job_alert_analytics';
import { isCurrentPageJobSearch } from '~/jobiqo/components/Job/Detail/utils';
import {
  excludeQueryParams,
  getQueryConditions,
  getUrlWithoutFragment,
} from '~/jobiqo/components/Search/utils/searchHelpers';
import { AppContext, PathContext } from '~/jobiqo/context/app';
import { AppContextInterface, PathContextInterface } from '~/jobiqo/models/app';
import DELETE_JOB_ALERT from '~/jobiqo/components/JobAlert/graphql/mutations/deleteJobAlert.gql';
import CREATE_JOB_ALERT from '~/jobiqo/components/JobAlert/graphql/mutations/createJobAlert.gql';
import CREATE_SIMILAR_JOB_ALERT from '~/jobiqo/components/JobAlert/graphql/mutations/createSimilarJobAlert.gql';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';

/**
 * The URL fragment.
 * If the applicant is signed in fragment indicates that the job alert is saved.
 * Otherwise, it indicates that the job alert signup modal is opened.
 */
export const SAVE_SEARCH_FRAGMENT = '#save-search';

export interface JobAlertContextInterface {
  /**
   * The querystring that will be used within create
   * job alert mutation.
   */
  querystring: string;
  /**
   * Currently selected facets.
   */
  selectedFacets: Facet[];
  /**
   * The flag to show modal.
   */
  showModal: boolean;
  /**
   * The actual job alert entity (null if none exists for current path).
   */
  jobAlert: JobAlert | null;
  /**
   * The job alert title.
   */
  jobAlertTitle: string;
  /**
   * The create job mutation is loading flag.
   */
  createJobAlertLoading: boolean;
  /**
   * SelectedFacets metadata.
   */
  setSelectedFacets: Dispatch<SetStateAction<Facet[]>>;
  /**
   * Querystring setter.
   */
  setQuerystring: Dispatch<SetStateAction<string>>;
  /**
   * Show confirm setter.
   */
  setShowModal: Dispatch<SetStateAction<boolean>>;
  /**
   * Toggle checked setter.
   */
  setJobAlert: Dispatch<SetStateAction<JobAlert | null>>;
  /**
   * The jobAlertTitle setter.
   */
  setJobAlertTitle: Dispatch<SetStateAction<string>>;
  /**
   * The update job alert mutation.
   */
  updateJobAlert: (variables?: any) => Promise<any>;
  /**
   * The jobId setter.
   */
  setJobId: Dispatch<SetStateAction<number | null>>;
}

/**
 * Get mutation variables depending if we are removing or adding a
 * job alert.
 */
function getVariables(
  name: string,
  path: string,
  querystring: any,
  jobAlert: JobAlert | null,
  jobSearchFacetsConfig: FacetConfig[],
  language?: String,
  additionalConditionGroups: any[] = [],
) {
  if (jobAlert) {
    return {
      variables: {
        id: jobAlert.id,
      },
      update(cache) {
        updateCache(cache);
      },
    };
  }
  return {
    variables: {
      data: {
        name,
        path,
        language,
        condition_group: {
          conjunction: 'AND',
          groups: [
            ...getQueryConditions(querystring, jobSearchFacetsConfig),
            ...additionalConditionGroups,
          ],
        },
      },
      update(cache) {
        updateCache(cache);
      },
    },
  };
}

/**
 * Updates job alert related cache entries.
 *
 * @param cache
 *   The cache to update.
 */
export function updateCache(cache) {
  cache.evict({ fieldName: 'getJobAlertById' });
  cache.evict({ fieldName: 'getJobAlerts' });
  cache.evict({ fieldName: 'getJobAlertsCount' });
  cache.evict({ fieldName: 'getJobAlertByPath' });
}

export const JobAlertContext: Context<JobAlertContextInterface> = createContext<
  JobAlertContextInterface
>({
  querystring: '',
  selectedFacets: [],
  showModal: false,
  jobAlert: null,
  jobAlertTitle: '',
  createJobAlertLoading: false,
  setSelectedFacets: () => {},
  setQuerystring: () => {},
  setShowModal: () => {},
  setJobAlert: () => {},
  setJobAlertTitle: () => {},
  updateJobAlert: () => new Promise(() => {}),
  setJobId: () => {},
});

interface JobAlertProviderProps {
  children: React.ReactNode;
  /**
   * Additional condition groups that should always be saved in the alert.
   */
  additionalConditionGroups?: any[];
  /**
   * Treat this provider as a job-search context even outside the configured jobs page.
   */
  forceJobSearchPage?: boolean;
  /**
   * Analytics page context override for job alert prompt/signup events.
   */
  pageType?: PageType;
}

export default function JobAlertProvider({
  children,
  additionalConditionGroups = [],
  forceJobSearchPage = false,
  pageType,
}: JobAlertProviderProps) {
  const { path }: PathContextInterface = useContext(PathContext);
  const { user }: UserContextInterface = useContext(UserContext);
  const { locale }: AppContextInterface = useContext(AppContext);
  const [selectedFacets, setSelectedFacets] = useState<Facet[]>([]);
  const [querystring, setQuerystring] = useState<string>('');
  const [showModal, setShowModal] = useState<boolean>(false);
  const [jobAlert, setJobAlert] = useState<JobAlert | null>(null);
  const [jobAlertTitle, setJobAlertTitle] = useState<string>('');
  const [jobId, setJobId] = useState<number | null>(null);
  const messagesService = useMessages();
  const flagService = useFlags();
  const router = useRouter();
  const intl = useIntl();
  const isJobSearchPage = forceJobSearchPage || isCurrentPageJobSearch();
  const jobAlertPageType =
    pageType ??
    (isJobSearchPage ? PAGE_TYPES.JOB_SEARCH : PAGE_TYPES.JOB_DETAIL);
  const { JOBS } = useFrontendConfig();
  const jobSearchFacetsConfig: FacetConfig[] = Object.values(JOBS.FACETS);

  const [updateJobAlertMutation, { loading }] = useMutation(
    !jobAlert
      ? // If we are on the job search page we are creating a job alert
        // based on the URL path, otherwise, we have to create a job
        // alert similar to the current job (jobId).
        isJobSearchPage
        ? CREATE_JOB_ALERT
        : CREATE_SIMILAR_JOB_ALERT
      : DELETE_JOB_ALERT,
    {
      update(cache) {
        updateCache(cache);
      },
      onCompleted: (result) => {
        setShowModal(false);

        if (selectedFacets && result.createJobAlert) {
          analyticsDispatch(
            user ? EVENTS.JOB_ALERT_SIGNUP : EVENTS.JOB_ALERT_SIGNUP_ANONYMOUS,
            getjobAlertDimensions(
              result.createJobAlert.alert,
              user ?? null,
              selectedFacets,
              (querystring as any).geo_location,
              jobAlertPageType,
            ),
          );
        }

        router
          .push(
            // If we have just created an alert append fragment, remove it otherwise.
            result.createJobAlert
              ? getUrlWithoutFragment(path)
              : path + SAVE_SEARCH_FRAGMENT,
            undefined,
            { shallow: true },
          )
          .then(() => showMutationMessage(result));
      },
      onError: () => {
        flagService.addFlag({
          text: intl.formatMessage({
            id: 'generic_error',
            defaultMessage: `There was an error`,
          }),
          type: 'danger',
        });
      },
    },
  );

  const showMutationMessage = (result: any) => {
    if (result.createJobAlert) {
      if (result.createJobAlert.errors.length > 0) {
        flagService.addFlag({
          text: result.createJobAlert.errors[0].message,
          type: 'danger',
        });
      } else {
        messagesService.addMessage({
          text: user
            ? intl.formatMessage({
                id: 'job_alert_save_anon',
                defaultMessage: `Your job alert was successfully created.`,
              })
            : intl.formatMessage({
                id: 'job_alert_save',
                defaultMessage: `Your job alert was successfully created.
                      You will soon receive an e-mail with a confirmation link to activate it.`,
              }),
          type: 'success',
          test_id: 'job_alert_save_success',
        });
      }
    } else if (result.deleteJobAlert) {
      if (result.deleteJobAlert.errors.length > 0) {
        flagService.addFlag({
          text: result.deleteJobAlert.errors[0].message,
          type: 'danger',
        });
      } else {
        messagesService.addMessage({
          text: intl.formatMessage({
            id: 'job_alert_remove',
            defaultMessage: `Your Search was removed`,
          }),
          type: 'success',
        });
      }
    }
  };

  const handleUpdateJobAlert = (params) => {
    // Create a job alert for path without jobId and page query params.
    const pathArgument = excludeQueryParams(getUrlWithoutFragment(path), [
      'jobId',
      'page',
    ]);

    return updateJobAlertMutation(
      params
        ? {
            ...params,
            variables: {
              data: {
                ...params.variables.data,
                path: pathArgument,
                condition_group: {
                  conjunction: 'AND',
                  groups: [
                    ...getQueryConditions(querystring, jobSearchFacetsConfig),
                    ...additionalConditionGroups,
                  ],
                },
                // If we are not on the job search page, we have to pass the
                // current job id to the mutation because we are creating
                // a similar job alert based on the job id.
                ...(!isJobSearchPage && { jobId }),
              },
            },
            update(cache) {
              updateCache(cache);
            },
          }
        : getVariables(
            jobAlertTitle,
            pathArgument,
            querystring,
            jobAlert,
            jobSearchFacetsConfig,
            locale,
            additionalConditionGroups,
          ),
    );
  };

  useEffect(() => {
    // Dispatch event every time we open a modal.
    if (showModal) {
      analyticsDispatch(
        EVENTS.JOB_ALERT_PROMPT,
        getjobAlertDimensions(
          jobAlert ?? null,
          user ?? null,
          selectedFacets,
          (querystring as any).geo_location,
          jobAlertPageType,
        ),
      );
    }
  }, [showModal]);

  useEffect(() => {
    // If the job alert exists, we want to make sure that there is a fragment in the URL.
    if (jobAlert && !path.includes(SAVE_SEARCH_FRAGMENT)) {
      router.push(path + SAVE_SEARCH_FRAGMENT, undefined, { shallow: true });
    } else if (jobAlert === null && path.includes(SAVE_SEARCH_FRAGMENT)) {
      router.push(getUrlWithoutFragment(path), undefined, { shallow: true });
    }
  }, [jobAlert]);

  return (
    <JobAlertContext.Provider
      value={{
        selectedFacets,
        querystring,
        showModal,
        jobAlert,
        jobAlertTitle,
        createJobAlertLoading: loading,
        updateJobAlert: handleUpdateJobAlert,
        setSelectedFacets,
        setQuerystring,
        setShowModal,
        setJobAlert,
        setJobAlertTitle,
        setJobId,
      }}
    >
      {children}
    </JobAlertContext.Provider>
  );
}
